import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnDestroy, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';
import * as Highcharts from 'highcharts';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import { SocketService, SocketServerEvent } from '../../../services/socket.service';
import { ServerTimeService } from '../../../services/server-time';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue, resolvePeriodForBlockLabel } from './peak-report.data';
import {
  applyPreviousValueMode,
  buildReportBlockGroups,
  commitReportAutocompleteValue,
  buildEditDataFormValue,
  clearTruthyFormControls,
  downloadReportBlob,
  extractReportBsCalendar,
  extractReportDateConversion,
  extractReportResponseValue,
  extractReportStringValue,
  filterReportOptions,
  findReportOptionByLabel,
  formatReportNumber,
  buildGroupedReportHierarchyNodes,
  buildLinkedReportHierarchyNodes,
  flattenReportHierarchyNodes,
  getReportArrayValue,
  getReportCellText,
  getNullableNumberValue,
  getReportDateLabel,
  getReportHierarchyNodeOrderKey,
  getReportRowBlockExtremeValue,
  getReportRecord,
  getReportRecordValue,
  handleReportAutocompleteSearch,
  hasTextValue,
  isLogsheetDataDetailCell,
  normalizeReportNumber,
  isOutsideElement,
  shouldFetchPreviousValue,
  toggleReportHierarchyRow,
} from '../shared/report-ui.helpers';
import {
  PeakReportDateConversionResponse,
  PeakReportBlockCell,
  PeakReportBlockDefinition,
  PeakReportBsCalendarCell,
  PeakReportBsCalendarResponse,
  PeakReportPreviousBlockValueResponse,
  PeakReportReportResponse,
  PeakReportChartResponse,
  PeakReportRow,
  PeakReportSummary,
  PeakReportSubstationOption,
} from './peak-report.interfaces';

type EditableCellKey = `${number}-${number}`;

interface PeakReportHierarchyNode {
  row: PeakReportRow;
  children: PeakReportHierarchyNode[];
}

interface PeakReportBlockGroup {
  period: PeakReportBlockDefinition['period'];
  label: string;
  span: number;
}

type PeakReportViewTab = 'generation' | 'summary';
type PeakReportDisplayMode = 'generation' | 'spill' | 'water-level' | 'interruption' | 'summary';

const SUMMARY_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-total-import',
  'row:power-total-export',
  'row:power-net-import',
  'row:power-system-load-actual',
  'row:power-interruption-tripping',
  'row:power-system-load-expected',
  'row:power-national-load-actual',
  'row:power-frequency-voltage-profile',
  'row:power-frequency-voltage-substation',
  'row:power-kabeli-corridor',
  'row:power-kathmandu-load',
  'row:power-spill',
  'row:power-water-level',
] as const;
const SUMMARY_ACCORDION_ORDER_MAP = new Map<string, number>(
  SUMMARY_ACCORDION_ORDER.map((key, index) => [key, index]),
);

@Component({
  selector: 'app-peak-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent],
  templateUrl: './peak-report.html',
  styleUrl: './peak-report.scss',
})
export class PeakReportPage implements OnInit, AfterViewInit, OnDestroy {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isChartLoading = false;
  protected isSavingCell = false;
  protected selectedViewTab: PeakReportViewTab = 'summary';
  protected detailMode: PeakReportDisplayMode = 'summary';

  protected blockColumns: PeakReportBlockDefinition[] = [];
  protected blockGroups: PeakReportBlockGroup[] = [];
  protected rows: PeakReportRow[] = [];
  protected summary: PeakReportSummary = {
    scheduled_mwh: 0,
    actual_mwh: 0,
    deviation_mwh: 0,
    row_count: 0,
  };

  protected substationOptions: PeakReportSubstationOption[] = [];
  protected filteredSubstationOptions: PeakReportSubstationOption[] = [];
  protected substationSearchTerm = '';
  protected selectedDateDisplayLabel = getTodayDateValue();
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: PeakReportBsCalendarResponse | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  protected editDataModalOpen = false;
  protected remarksForm: UntypedFormGroup;
  protected socketId: string | null = null;

  @ViewChild('tableWrap') private readonly tableWrap?: ElementRef<HTMLDivElement>;
  @ViewChild('morningPeakChartContainer') private readonly morningPeakChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('nightPeakChartContainer') private readonly nightPeakChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('hiddenDateInput') private readonly hiddenDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bsDatePickerShell') private readonly bsDatePickerShell?: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private hierarchyRoots: PeakReportHierarchyNode[] = [];
  private reportRowsCache: PeakReportRow[] = [];
  private expandedRowKeys = new Set<string>();
  private pendingViewStateRestore: {
    expandedRowKeys: string[];
    scrollTop: number;
    scrollLeft: number;
  } | null = null;
  private pendingRemarksContext: {
    station_id: number;
    block_no: number;
    block_value: number | null;
    remarks: string | null;
  } | null = null;
  private selectedDateRequestValue = getTodayDateValue();
  private selectedDateAdValue = getTodayDateValue();
  private morningPeakChart?: Highcharts.Chart;
  private nightPeakChart?: Highcharts.Chart;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly socketSvc: SocketService,
  ) {
    this.filtersForm = this.fb.group({
      date: [getTodayDateValue(), Validators.required],
      substation_id: [null],
      show_values: [null],
    });
    this.remarksForm = this.fb.group({
      data_value: [''],
      copy_previous_block_value: [false],
      carry_over_previous_day_values: [false],
      remarks: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.loadSubstationOptions();
    this.listenToSocketEvents();
    this.listenToPreferenceChanges();
    await this.handleDateSelectionChange();
  }

  ngAfterViewInit(): void {
    this.loadPeakChart();
  }

  ngOnDestroy(): void {
    this.morningPeakChart?.destroy();
    this.nightPeakChart?.destroy();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filtersForm.patchValue(
      {
        date: getTodayDateValue(),
        substation_id: null,
        show_values: null,
      },
      { emitEvent: false },
    );
    this.substationSearchTerm = '';
    this.refreshGeneratorOptionsForDetailMode(false);
    this.bsCalendarOpen = false;
    void this.handleDateSelectionChange();
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    const payload = this.buildReportPayload();
    this.isExporting = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_PEAK_REPORT, ApiMethod.POST, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName()),
      });
  }

  protected trackByBlock(_: number, item: PeakReportBlockDefinition): number {
    return item.block_no;
  }

  protected trackByBlockGroup(_: number, item: PeakReportBlockGroup): string {
    return `${item.period}-${item.span}`;
  }

  protected trackByRow(_: number, item: PeakReportRow): string {
    return item.row_key ?? `${item.row_kind ?? 'data'}-${item.station_id}`;
  }

  protected trackBySubstation(_: number, item: PeakReportSubstationOption): number {
    return item.id;
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    handleReportAutocompleteSearch({
      query: event?.query,
      form: this.filtersForm,
      controlName: 'substation_id',
      filterOptions: (query) => this.filterSubstationAutocomplete(query),
      findOption: (label) => this.findSubstationByLabel(label),
      normalizeNumber: (value) => this.normalizeNumber(value),
    });
  }

  protected onSubstationSearchBlur(): void {
    this.commitSubstationAutocompleteValue();
  }

  protected getSelectedShowValuesLabel(): string {
    const value = this.filtersForm.get('show_values')?.value;
    const label = typeof value === 'string' && value ? value : 'Select Value';
    return label;
  }

  protected getFilterDateLabel(): string {
    return getReportDateLabel(this.selectedDateDisplayLabel, this.selectedDateRequestValue, this.selectedDateAdValue);
  }

  protected getHeaderSummary(): string {
    const count = this.summary.row_count;
    const blockCount = this.blockColumns.length;
    return `Showing ${count} generator${count === 1 ? '' : 's'} • ${blockCount} peak block${blockCount === 1 ? '' : 's'}`;
  }

  protected isPeakBlock(cell: PeakReportBlockCell): boolean {
    return cell.is_peak === true;
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected setViewTab(viewTab: PeakReportViewTab): void {
    const shouldResetAccordionState = this.selectedViewTab !== viewTab;
    this.selectedViewTab = viewTab;
    this.pendingViewStateRestore = null;
    this.detailMode = viewTab === 'summary' ? 'summary' : 'generation';
    if (!shouldResetAccordionState) {
      return;
    }

    this.loadReport(this.rows.length > 0);
  }

  protected handleDetailModeChange(mode: PeakReportDisplayMode): void {
    if (this.detailMode === mode) {
      return;
    }

    this.detailMode = mode;
    if (mode === 'summary') {
      this.setViewTab('summary');
      return;
    }

    if (this.selectedViewTab === 'summary') {
      this.setViewTab('generation');
    }

    this.refreshGeneratorOptionsForDetailMode();
    this.loadReport(this.rows.length > 0);
  }

  protected isDetailModeActive(mode: PeakReportDisplayMode): boolean {
    return this.detailMode === mode;
  }

  protected getCellText(cell: PeakReportBlockCell): string {
    return getReportCellText(cell);
  }

  protected isEditedCell(cell: PeakReportBlockCell): boolean {
    return isLogsheetDataDetailCell(cell);
  }

  protected getTotalCellText(value: number): string {
    return this.formatNumber(value);
  }

  protected getRowMaxText(row: PeakReportRow): string {
    return this.formatNumber(this.getRowBlockMaxValue(row));
  }

  protected getRowMinText(row: PeakReportRow): string {
    return this.formatNumber(this.getRowBlockMinValue(row));
  }

  protected getInstalledCapacityText(row: PeakReportRow): string {
    return this.formatNumber(row.installed_capacity ?? 0);
  }

  protected isCellInteractive(row: PeakReportRow, cell: PeakReportBlockCell): boolean {
    return false;
  }

  protected isSectorTotalRow(row: PeakReportRow): boolean {
    return this.isHierarchyTotalRow(row);
  }

  protected isHierarchyTotalRow(row: PeakReportRow): boolean {
    if (row.has_children) {
      return true;
    }

    return !this.isDetailRowKind(row.row_kind);
  }

  protected hasHierarchyChildren(row: PeakReportRow): boolean {
    if (!row.row_key || !row.has_children) {
      return false;
    }

    return true;
  }

  protected isHierarchyExpanded(row: PeakReportRow): boolean {
    if (!row.row_key) {
      return false;
    }

    return this.expandedRowKeys.has(row.row_key);
  }

  protected toggleHierarchyRow(row: PeakReportRow): void {
    if (!toggleReportHierarchyRow(row, this.expandedRowKeys)) {
      return;
    }

    this.rows = this.buildRowsFromCurrentState();
  }

  protected beginCellEdit(row: PeakReportRow, cell: PeakReportBlockCell): void {
    if (!this.isCellInteractive(row, cell)) {
      return;
    }

    this.openEditDataModal(row, cell);
  }

  protected closeEditDataModal(): void {
    this.editDataModalOpen = false;
    this.pendingRemarksContext = null;
    this.remarksForm.reset(
      { data_value: '', copy_previous_block_value: false, carry_over_previous_day_values: false, remarks: '' },
      { emitEvent: false },
    );
  }

  protected saveEditData(): void {
    if (!this.pendingRemarksContext) {
      this.closeEditDataModal();
      return;
    }

    const context = { ...this.pendingRemarksContext };
    const formValue = this.remarksForm.getRawValue();
    const remarks = typeof formValue.remarks === 'string' ? formValue.remarks.trim() : '';
    const dataValue = this.normalizeNullableNumber(formValue.data_value);
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: context.station_id,
      block_no: context.block_no,
      block_value: dataValue,
      remarks: remarks || null,
      detail_mode: this.detailMode,
    };

    this.updateLocalCellValue(context.station_id, context.block_no, dataValue, remarks || null);
    this.isSavingCell = true;
    this.httpSvc
      .requestCall(AuthEndPoints.UPDATE_LOG_SHEET_CELL, ApiMethod.PATCH, payload, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSavingCell = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this.closeEditDataModal();
          this.loadReport(true);
        },
        error: () => {
          this.loadReport(true);
        },
      });
  }

  protected onEditDataValueInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = target?.value ?? '';
    if (hasTextValue(nextValue)) {
      clearTruthyFormControls(this.remarksForm, ['copy_previous_block_value', 'carry_over_previous_day_values']);
    }
  }

  protected onPreviousValueModeChange(mode: 'copy' | 'carry'): void {
    applyPreviousValueMode(this.remarksForm, mode);
  }

  protected arePreviousValueControlsDisabled(): boolean {
    const dataValue = this.remarksForm.get('data_value')?.value;
    return hasTextValue(dataValue);
  }

  protected onCopyPreviousBlockValueChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'copy_previous_block_value', 'carry_over_previous_day_values', Boolean(context)) || !context) {
      return;
    }

    this.fetchPreviousBlockValue(context.station_id, context.block_no);
  }

  protected onCarryOverPreviousDayValuesChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'carry_over_previous_day_values', 'copy_previous_block_value', Boolean(context)) || !context) {
      return;
    }

    this.fetchPreviousDayBlockValue(context.station_id, context.block_no);
  }

  protected formatNumber(value: number | null): string {
    return formatReportNumber(value);
  }

  protected getEmptyColspan(): number {
    return this.blockColumns.length + 1;
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    if (!this.bsCalendarOpen) {
      return;
    }

    if (!isOutsideElement(event.target, this.bsDatePickerShell?.nativeElement)) {
      return;
    }

    this.closeBsDatePicker();
  }

  private collapseAllAccordions(): void {
    this.expandedRowKeys.clear();
    this.rows = this.buildRowsFromCurrentState();
  }

  private bindFilterListeners(): void {
    this.filtersForm.get('date')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleDateSelectionChange(this.rows.length > 0);
      });

    for (const controlName of ['substation_id', 'show_values']) {
      this.filtersForm.get(controlName)?.valueChanges
        .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.loadReport(this.rows.length > 0));
    }

    this.remarksForm.get('copy_previous_block_value')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCopyPreviousBlockValueChange());

    this.remarksForm.get('carry_over_previous_day_values')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCarryOverPreviousDayValuesChange());
  }

  private listenToSocketEvents(): void {
    this.socketSvc.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleSocketEvent(event));
    this.socketSvc.socketId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((socketId) => {
        this.socketId = socketId;
        this.cdr.detectChanges();
      });
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        this.closeBsDatePicker();
        this.handleDateSelectionChange(this.rows.length > 0);
      });
  }

  private handleSocketEvent(event: SocketServerEvent): void {
    if (event.event === 'scada_sync_completed') {
      this.loadReport(true);
    }
  }

  private loadSubstationOptions(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '', false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.substationOptions = this.extractSubstationOptions(response);
          this.refreshGeneratorOptionsForDetailMode();
          this.cdr.detectChanges();
        },
        error: () => {
          this.substationOptions = [];
          this.filteredSubstationOptions = [];
          this.cdr.detectChanges();
        },
      });
  }

  private filterSubstationAutocomplete(query: string): void {
    const sourceOptions = this.getVisibleGeneratorOptions();
    this.filteredSubstationOptions = filterReportOptions(sourceOptions, query);
  }

  private commitSubstationAutocompleteValue(): void {
    commitReportAutocompleteValue({
      searchTerm: this.substationSearchTerm,
      form: this.filtersForm,
      controlName: 'substation_id',
      findOption: (label) => this.findSubstationByLabel(label),
      normalizeNumber: (value) => this.normalizeNumber(value),
      setSearchTerm: (value) => {
        this.substationSearchTerm = value;
      },
    });
  }

  private findSubstationByLabel(label: string): PeakReportSubstationOption | null {
    return findReportOptionByLabel(this.getVisibleGeneratorOptions(), this.getVisibleGeneratorOptions(), label);
  }

  private loadReport(preserveViewState = false): void {
    if (preserveViewState) {
      this.captureViewStateForRestore();
    } else {
      this.pendingViewStateRestore = null;
    }

    const payload = this.buildReportPayload();
    this.loadPeakChart();
    this.isLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_PEAK_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const report = this.extractReport(response);
          this.applyReport(report);
        },
        error: () => {
          if (preserveViewState) {
            this.restoreViewStateAfterRefresh();
            return;
          }

          this.applyReport(null);
        },
      });
  }

  private loadPeakChart(): void {
    if (this.isChartLoading) {
      return;
    }

    this.isChartLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_PEAK_REPORT_CHART, ApiMethod.POST, this.buildReportPayload(), false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isChartLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => this.renderPeakCharts(this.extractChartReport(response)),
        error: () => this.renderPeakCharts(null),
      });
  }

  private renderPeakCharts(chartReport: PeakReportChartResponse | null): void {
    this.morningPeakChart = this.renderPeakChart(chartReport, this.morningPeakChartContainer, 'day', this.morningPeakChart);
    this.nightPeakChart?.destroy();
    this.nightPeakChart = undefined;
  }

  private renderPeakChart(
    chartReport: PeakReportChartResponse | null,
    containerRef: ElementRef<HTMLDivElement> | undefined,
    period: PeakReportBlockDefinition['period'],
    existingChart: Highcharts.Chart | undefined,
  ): Highcharts.Chart | undefined {
    const container = containerRef?.nativeElement;
    if (!container || !chartReport) {
      existingChart?.destroy();
      return undefined;
    }

    const dates = chartReport.dates ?? [];
    const availabilityKey = period === 'morning' ? 'morning_available' : 'night_available';
    const hasData = dates.some((dateItem) => (
      period === 'day'
        ? dateItem.blocks.some((item) => item.period === period && item.value !== null)
        : dateItem[availabilityKey] === true
    ));
    const seriesColor = this.getPeakChartSeriesColor(period);
    const series: Highcharts.SeriesLineOptions[] = hasData
      ? [{
          type: 'line',
          name: this.getPeriodTitle(period),
          color: seriesColor,
          data: dates.map((dateItem) => {
            const periodBlocks = dateItem.blocks.filter((item) => item.period === period);
            if (period === 'day' ? !periodBlocks.length : dateItem[availabilityKey] !== true) {
              return { y: null };
            }

            const block = periodBlocks.find((item) => item.is_peak)
              ?? periodBlocks.reduce<PeakReportBlockCell | null>(
                (peakBlock, currentBlock) => (
                  currentBlock.value !== null && currentBlock.value > (peakBlock?.value ?? -Infinity)
                    ? currentBlock
                    : peakBlock
                ),
                null,
              );
            return {
              y: block?.value ?? null,
              custom: {
                date: dateItem.date_label,
                block: block?.label ?? '-',
              },
            };
          }),
      }]
      : [];

    existingChart?.destroy();
    return Highcharts.chart(container, {
      chart: { type: 'line', backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories: dates.map((item) => item.date_label),
        title: { text: 'Date', style: { color: '#334155' } },
        lineColor: '#e2e8f0',
        tickColor: '#e2e8f0',
        labels: { style: { color: '#334155' } },
      },
      yAxis: {
        title: { text: 'Demand (MW)', style: { color: '#334155' } },
        gridLineColor: '#eef2f7',
        labels: { style: { color: '#334155' } },
      },
      legend: {
        itemStyle: { color: '#334155', fontWeight: '600' },
        itemHoverStyle: { color: '#0f172a' },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: '#ffffff',
        borderColor: 'rgba(99, 102, 241, 0.25)',
        borderRadius: 9,
        borderWidth: 1,
        padding: 0,
        shadow: true,
        style: { color: '#0f172a' },
        formatter: function () {
          const context = this as unknown as { point: Highcharts.Point & { custom?: { date?: string; block?: string } } };
          const point = context.point;
          return `<table class="peak-report-chart__tooltip-table"><tbody><tr><th>Date:</th><td>${point.custom?.date ?? '-'}</td></tr><tr><th>Peak Time:</th><td>${point.custom?.block ?? '-'}</td></tr><tr><th>Value(MW):</th><td>${point.y ?? '-'}</td></tr></tbody></table>`;
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: { enabled: true, radius: 4 },
        },
      },
      series,
    } as Highcharts.Options);
  }

  private getPeakChartSeriesColor(period: PeakReportBlockDefinition['period']): string {
    if (period === 'day') {
      return '#16a34a';
    }

    if (period === 'morning') {
      return '#2563eb';
    }

    return '#f59e0b';
  }

  private isDetailRowKind(rowKind: PeakReportRow['row_kind']): boolean {
    return rowKind === 'data' || rowKind === 'interruption-line' || rowKind === 'interruption-substation' ||
      rowKind === 'power-thermal-generator' || rowKind === 'power-water-level-generator' ||
      rowKind === 'power-kabeli-corridor-generator' || rowKind === 'frequency-voltage-substation';
  }

  private applyReport(report: PeakReportReportResponse | null): void {
    if (!report) {
      this.blockColumns = [];
      this.blockGroups = [];
      this.rows = [];
      this.hierarchyRoots = [];
      this.reportRowsCache = [];
      this.expandedRowKeys.clear();
      this.summary = {
        scheduled_mwh: 0,
        actual_mwh: 0,
        deviation_mwh: 0,
        row_count: 0,
      };
      return;
    }

    this.blockColumns = report.blocks;
    this.blockGroups = this.buildBlockGroups(report.blocks);
    this.reportRowsCache = report.rows.map((row) => this.cloneReportRow(row));
    this.hierarchyRoots = this.buildHierarchyNodes(this.reportRowsCache.filter((row) => !row.force_flat));
    const shouldRestoreViewState = Boolean(this.pendingViewStateRestore);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.getCollapsedSummaryRowKeys();
    }
    this.rows = this.buildRowsFromCurrentState();
    this.summary = report.summary;
    if (!this.preferencesSvc.showBsDate && report.date_label) {
      this.selectedDateDisplayLabel = report.date_label;
    }

    if (shouldRestoreViewState) {
      this.restoreViewStateAfterRefresh();
    } else {
      this.pendingViewStateRestore = null;
    }
  }

  private openEditDataModal(row: PeakReportRow, cell: PeakReportBlockCell): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      block_no: cell.block_no,
      block_value: getNullableNumberValue(cell.value),
      remarks: cell.remarks ?? null,
    };
    this.remarksForm.reset(buildEditDataFormValue(cell.value, cell.remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private fetchPreviousBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_BLOCK_VALUE, stationId, blockNo, 'copy_previous_block_value');
  }

  private fetchPreviousDayBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_DAY_BLOCK_VALUE, stationId, blockNo, 'carry_over_previous_day_values');
  }

  private fetchPreviousValue(endpoint: string, stationId: number, blockNo: number, resetControlName: string): void {
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: stationId,
      block_no: blockNo,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.applyPreviousBlockValue(response);
        },
        error: () => {
          this.remarksForm.patchValue({ [resetControlName]: false }, { emitEvent: false });
        },
      });
  }

  private applyPreviousBlockValue(response: unknown): void {
    const previous = this.extractPreviousBlockValue(response);
    if (previous?.data_value !== null && previous?.data_value !== undefined) {
      this.remarksForm.patchValue({ data_value: String(previous.data_value) }, { emitEvent: false });
    }
    if (previous?.remarks !== null && previous?.remarks !== undefined) {
      this.remarksForm.patchValue({ remarks: previous.remarks }, { emitEvent: false });
    }
  }

  private updateLocalCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    const updateCell = (row: PeakReportRow): void => {
      if (row.station_id !== stationId) {
        return;
      }

      row.blocks = row.blocks.map((cell) => {
        if (cell.block_no !== blockNo) {
          return cell;
        }

        return {
          ...cell,
          value: blockValue,
          remarks,
          source: 'logsheet_data_details',
        };
      });
    };

    const traverse = (nodes: PeakReportHierarchyNode[]): void => {
      for (const node of nodes) {
        updateCell(node.row);
        if (node.children.length) {
          traverse(node.children);
        }
      }
    };

    traverse(this.hierarchyRoots);
    this.updateCachedReportCellValue(stationId, blockNo, blockValue, remarks);
    this.rows = this.buildRowsFromCurrentState();
  }

  private updateCachedReportCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    for (const row of this.reportRowsCache) {
      if (row.station_id !== stationId) {
        continue;
      }

      row.blocks = row.blocks.map((cell) => {
        if (cell.block_no !== blockNo) {
          return cell;
        }

        return {
          ...cell,
          value: blockValue,
          remarks,
          source: 'logsheet_data_details',
        };
      });
    }
  }

  private cloneReportRow(row: PeakReportRow): PeakReportRow {
    return {
      ...row,
      blocks: row.blocks.map((cell) => ({ ...cell })),
    };
  }

  private extractPreviousBlockValue(response: unknown): PeakReportPreviousBlockValueResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      data_value: this.normalizeNullableNumber(value['data_value']),
      remarks: this.extractStringValue(value, 'remarks'),
      previous_block_no: this.normalizeNullableNumber(value['previous_block_no']),
      source: this.extractStringValue(value, 'source'),
    };
  }

  private buildReportPayload(): Record<string, unknown> {
    return {
      data_date: this.getSelectedDateValue(),
      entity_id: null,
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      show_values: this.normalizeSelectedShowValues(),
      detail_mode: this.detailMode,
      bs_ad_key: this.getDatePreferenceKey(),
      selected_view_tab: this.selectedViewTab,
    };
  }

  private getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  private normalizeSelectedShowValues(): string | null {
    const value = this.filtersForm.get('show_values')?.value;
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    return value.trim();
  }

  private captureViewStateForRestore(): void {
    const tableWrap = this.tableWrap?.nativeElement ?? null;
    this.pendingViewStateRestore = {
      expandedRowKeys: Array.from(this.expandedRowKeys),
      scrollTop: tableWrap?.scrollTop ?? 0,
      scrollLeft: tableWrap?.scrollLeft ?? 0,
    };
  }

  private restoreViewStateAfterRefresh(): void {
    const snapshot = this.pendingViewStateRestore;
    if (!snapshot) {
      return;
    }

    const restore: any = () => {
      this.expandedRowKeys = new Set(snapshot.expandedRowKeys);
      this.rows = this.buildRowsFromCurrentState();

      const tableWrap = this.tableWrap?.nativeElement ?? null;
      if (tableWrap) {
        tableWrap.scrollTop = snapshot.scrollTop;
        tableWrap.scrollLeft = snapshot.scrollLeft;
      }

      this.pendingViewStateRestore = null;
      this.cdr.detectChanges();
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
      return;
    }

    setTimeout(restore, 0);
  }

  private isCurrentDateSelected(): boolean {
    if (this.preferencesSvc.showBsDate) {
      const currentBsDate = this.serverTimeSvc.getCurrentBsDateValue();
      return Boolean(currentBsDate && this.getSelectedDateValue() === currentBsDate);
    }

    return this.getSelectedDateValue() === getTodayDateValue();
  }

  private buildHierarchyNodes(rows: PeakReportRow[]): PeakReportHierarchyNode[] {
    const normalizedRows: PeakReportRow[] = rows.map((row) => ({
      ...row,
      row_kind: row.row_kind === 'power-small' || row.parent_row_key ? row.row_kind : 'data',
      row_level: row.row_kind === 'power-small' || row.parent_row_key ? row.row_level : 3,
      row_key: row.row_kind === 'power-small' || row.parent_row_key ? row.row_key : `data-${row.station_id}`,
      has_children: false,
    }));

    return buildGroupedReportHierarchyNodes({
      rows: normalizedRows,
      isSummaryView: this.selectedViewTab === 'summary',
      normalizeHierarchyLabel: (value) => this.normalizeHierarchyLabel(value),
      normalizeOptionalHierarchyLabel: (value) => this.normalizeOptionalHierarchyLabel(value),
      isFuelHierarchyLabel: (value) => this.isFuelHierarchyLabel(value),
      isMissingHierarchyLabel: (value) => this.isMissingHierarchyLabel(value),
      sortNodes: (nodes) => this.sortHierarchyNodesForCurrentMode(nodes),
      buildAggregateRow: (rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren) =>
        this.buildAggregateRow(rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren),
    });
  }

  private buildRowsFromCurrentState(): PeakReportRow[] {
    const flatNodes = this.sortHierarchyNodesForCurrentMode(this.buildFlatSummaryNodes());
    const rootNodes = this.selectedViewTab === 'summary'
      ? this.sortHierarchyNodesForCurrentMode([...this.hierarchyRoots, ...flatNodes])
      : [...this.hierarchyRoots, ...flatNodes];

    return [
      ...this.flattenHierarchyNodes(rootNodes, this.expandedRowKeys),
    ];
  }

  private buildFlatSummaryNodes(): PeakReportHierarchyNode[] {
    return this.buildLinkedRowNodes(this.reportRowsCache.filter((row) => row.force_flat));
  }

  private buildLinkedRowNodes(rows: PeakReportRow[]): PeakReportHierarchyNode[] {
    return buildLinkedReportHierarchyNodes(rows, (nodes) => this.sortHierarchyNodesForCurrentMode(nodes));
  }

  private sortHierarchyNodesForCurrentMode(nodes: PeakReportHierarchyNode[]): PeakReportHierarchyNode[] {
    if (this.selectedViewTab !== 'summary') {
      return nodes;
    }

    return [...nodes].sort((left, right) => this.getHierarchyNodeOrder(left) - this.getHierarchyNodeOrder(right));
  }

  private getHierarchyNodeOrder(node: PeakReportHierarchyNode): number {
    const key = this.getHierarchyNodeOrderKey(node.row);
    return SUMMARY_ACCORDION_ORDER_MAP.get(key) ?? SUMMARY_ACCORDION_ORDER_MAP.size + 100;
  }

  private getHierarchyNodeOrderKey(row: PeakReportRow): string {
    return getReportHierarchyNodeOrderKey(row);
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: PeakReportRow[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren = false,
  ): PeakReportRow {
    const blocks = this.blockColumns.map((block) => {
      const total = rows.reduce((sum, row) => {
        const cell = row.blocks.find((item) => item.block_no === block.block_no);
        return sum + (cell?.value ?? 0);
      }, 0);

      return {
        block_no: block.block_no,
        label: block.label,
        period: block.period,
        value: Number(total.toFixed(5)),
        remarks: null,
        source: null,
        editable_id: null,
        is_peak: block.is_peak === true,
      };
    });

    const scheduled_mwh = rows.reduce((sum, row) => sum + row.scheduled_mwh, 0);
    const actual_mwh = rows.reduce((sum, row) => sum + row.actual_mwh, 0);
    const deviation_mwh = rows.reduce((sum, row) => sum + row.deviation_mwh, 0);
    const installed_capacity = rows.reduce((sum, row) => sum + (row.installed_capacity ?? 0), 0);
    const installedCapacityLabel = this.formatNumber(Number(installed_capacity.toFixed(5)));

    return {
      station_id: 0,
      station_name: `${label} (${installedCapacityLabel})`,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      blocks,
      scheduled_mwh: Number(scheduled_mwh.toFixed(5)),
      actual_mwh: Number(actual_mwh.toFixed(5)),
      deviation_mwh: Number(deviation_mwh.toFixed(5)),
      installed_capacity: Number(installed_capacity.toFixed(5)),
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
    };
  }

  private getRowBlockMaxValue(row: PeakReportRow): number | null {
    return getReportRowBlockExtremeValue(row, 'max');
  }

  private getRowBlockMinValue(row: PeakReportRow): number | null {
    return getReportRowBlockExtremeValue(row, 'min');
  }

  private flattenHierarchyNodes(nodes: PeakReportHierarchyNode[], expandedKeys: Set<string>): PeakReportRow[] {
    return flattenReportHierarchyNodes(nodes, expandedKeys);
  }

  private getDefaultExpandedRowKeys(nodes: PeakReportHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    if (this.selectedViewTab === 'summary') {
      return expandedKeys;
    }

    for (const node of nodes) {
      if (!node.row.has_children || !node.row.row_key) {
        continue;
      }

      if ((node.row.row_level ?? 0) <= 1) {
        expandedKeys.add(node.row.row_key);
      }

      for (const child of node.children) {
        if (child.row.row_key && this.shouldExpandPowerModeChild(child)) {
          expandedKeys.add(child.row.row_key);
        }
      }
    }

    return expandedKeys;
  }

  private getCollapsedSummaryRowKeys(): Set<string> {
    return new Set<string>();
  }

  private shouldExpandPowerModeChild(child: PeakReportHierarchyNode): boolean {
    return Boolean(
      child.row.has_children &&
      child.row.row_key &&
      (child.row.row_level ?? 0) <= 1 &&
      this.hasSubcategoryChildren(child),
    );
  }

  private hasSubcategoryChildren(node: PeakReportHierarchyNode): boolean {
    return node.children.some((grandchild) => grandchild.row.row_kind === 'subcat-total');
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate || 'Unassigned';
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate && !this.isMissingHierarchyLabel(candidate) ? candidate : null;
  }

  private isFuelHierarchyLabel(value: string | null | undefined): boolean {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return false;
    }

    return !this.isMissingHierarchyLabel(normalized);
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return candidate === '' || candidate === 'na' || candidate === 'n/a' || candidate === 'null' || candidate === '-';
  }

  private buildBlockGroups(blocks: PeakReportBlockDefinition[]): PeakReportBlockGroup[] {
    return buildReportBlockGroups(blocks, (period) => this.getPeriodTitle(period));
  }

  private getPeriodTitle(period: PeakReportBlockDefinition['period']): string {
    switch (period) {
      case 'morning':
        return 'Morning Peak';
      case 'day':
        return 'Source Peak';
      case 'night':
        return 'Night Peak';
      default:
        return 'Blocks';
    }
  }

  private resolveBlockPeriod(period: string | null | undefined, label: string | null | undefined): PeakReportBlockDefinition['period'] {
    const normalizedPeriod = typeof period === 'string' ? period.trim().toLowerCase() : '';
    if (normalizedPeriod === 'morning' || normalizedPeriod === 'day' || normalizedPeriod === 'night') {
      return normalizedPeriod;
    }

    if (typeof label === 'string' && label.trim()) {
      return resolvePeriodForBlockLabel(label.trim());
    }

    return 'morning';
  }

  private extractReport(response: unknown): PeakReportReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const blocks = this.extractBlockDefinitions(value);
    const rows = this.extractRows(value);
    const summary = this.extractSummary(value);
    const dataDate = this.extractStringValue(value, 'data_date') ?? this.getSelectedDateValue();
    const dateLabel = this.preferencesSvc.formatDateOnly(dataDate);

    return {
      data_date: dataDate,
      date_label: dateLabel,
      blocks,
      rows,
      summary,
      filters: {
        entity_id: null,
        substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      },
    };
  }

  private extractChartReport(response: unknown): PeakReportChartResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const dates = this.getArrayValue(value, 'dates').map((item) => {
      const record = this.getRecord(item);
      const blocks = this.getArrayValue(record, 'blocks').map((blockItem) => {
        const block = this.getRecord(blockItem);
        return {
          block_no: this.normalizeNumber(block['block_no']) ?? 0,
          label: this.extractStringValue(block, 'label') ?? '-',
          period: this.resolveBlockPeriod(this.extractStringValue(block, 'period'), this.extractStringValue(block, 'label')),
          value: this.normalizeNullableNumber(block['value']),
          remarks: null,
          source: null,
          editable_id: null,
          is_peak: block['is_peak'] === true,
        } as PeakReportBlockCell;
      });
      return {
        date: this.extractStringValue(record, 'date') ?? '',
        date_label: this.extractStringValue(record, 'date_label') ?? '-',
        blocks,
        morning_available: record['morning_available'] === true,
        night_available: record['night_available'] === true,
      };
    });

    return {
      start_date: this.extractStringValue(value, 'start_date') ?? '',
      end_date: this.extractStringValue(value, 'end_date') ?? '',
      dates,
    };
  }

  private extractBlockDefinitions(value: unknown): PeakReportBlockDefinition[] {
    const rows = this.getArrayValue(value, 'blocks');
    if (!rows.length) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
        return {
          block_no: this.normalizeNumber(record['block_no']) ?? 0,
          label: this.extractStringValue(record, 'label') ?? '',
          period: this.resolveBlockPeriod(this.extractStringValue(record, 'period'), this.extractStringValue(record, 'label')),
          is_peak: record['is_peak'] === true,
        };
    });
  }

  private extractRows(value: unknown): PeakReportRow[] {
    const rows = this.getArrayValue(value, 'rows');
    if (!rows.length) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      const blocks = this.getArrayValue(record, 'blocks').map((blockItem) => {
        const blockRecord = this.getRecord(blockItem);
        return {
          block_no: this.normalizeNumber(blockRecord['block_no']) ?? 0,
          label: this.extractStringValue(blockRecord, 'label') ?? '',
          period: this.resolveBlockPeriod(this.extractStringValue(blockRecord, 'period'), this.extractStringValue(blockRecord, 'label')),
          value: this.normalizeNullableNumber(blockRecord['value']),
          remarks: this.extractStringValue(blockRecord, 'remarks'),
          source: this.extractStringValue(blockRecord, 'source') as PeakReportBlockCell['source'],
          editable_id: this.normalizeNullableNumber(blockRecord['editable_id']),
          is_peak: blockRecord['is_peak'] === true,
        };
      });

      return {
        station_id: this.normalizeNumber(record['station_id']) ?? 0,
        station_name: this.extractStringValue(record, 'station_name') ?? '-',
        station_code: this.extractStringValue(record, 'station_code'),
        owner: this.extractStringValue(record, 'owner'),
        source_category: this.extractStringValue(record, 'source_category'),
        sub_category: this.extractStringValue(record, 'sub_category'),
        blocks,
        scheduled_mwh: this.normalizeNumber(record['scheduled_mwh']) ?? 0,
        actual_mwh: this.normalizeNumber(record['actual_mwh']) ?? 0,
        deviation_mwh: this.normalizeNumber(record['deviation_mwh']) ?? 0,
        installed_capacity: this.normalizeNullableNumber(record['installed_capacity']),
        row_key: this.extractStringValue(record, 'row_key') ?? `data-${this.normalizeNumber(record['station_id']) ?? 0}`,
        row_kind: (this.extractStringValue(record, 'row_kind') as PeakReportRow['row_kind'] | null) ?? 'data',
        row_level: this.normalizeNumber(record['row_level']) ?? 3,
        has_children: Boolean(record['has_children']),
        force_expand_children: Boolean(record['force_expand_children']),
        force_flat: Boolean(record['force_flat']),
        parent_row_key: this.extractStringValue(record, 'parent_row_key'),
        row_theme: this.extractStringValue(record, 'row_theme'),
        is_small_power: Boolean(record['is_small_power']),
      };
    });
  }

  private extractSummary(value: unknown): PeakReportSummary {
    const summaryRecord = this.getRecordValue(value, 'summary');
    const record = this.getRecord(summaryRecord);
    return {
      scheduled_mwh: this.normalizeNumber(record['scheduled_mwh']) ?? 0,
      actual_mwh: this.normalizeNumber(record['actual_mwh']) ?? 0,
      deviation_mwh: this.normalizeNumber(record['deviation_mwh']) ?? 0,
      row_count: this.normalizeNumber(record['row_count']) ?? 0,
    };
  }

  private extractSubstationOptions(response: unknown): PeakReportSubstationOption[] {
    const value = this.extractResponseValue(response);
    const rows = this.getArrayValue(value, 'generators');
    return rows.map((item) => {
      const record = this.getRecord(item);
      const generatorName = this.extractStringValue(record, 'generator_name') ?? this.extractStringValue(record, 'name') ?? '-';
      const scadaStationCode = this.extractStringValue(record, 'scada_station_code') ?? this.extractStringValue(record, 'station_code');
      return {
        id: this.normalizeNumber(record['id']) ?? 0,
        name: generatorName,
        station_code: scadaStationCode,
        generator_name: generatorName,
        project_unique_code: this.extractStringValue(record, 'project_unique_code'),
        scada_station_code: scadaStationCode,
        other_parameter: this.extractStringValue(record, 'other_parameter'),
      };
    });
  }

  private refreshGeneratorOptionsForDetailMode(clearInvalidSelection = true): void {
    const visibleOptions = this.getVisibleGeneratorOptions();
    this.filteredSubstationOptions = [...visibleOptions];

    if (!this.isWaterLevelDetailMode()) {
      return;
    }

    const selectedId = this.normalizeNumber(this.filtersForm.get('substation_id')?.value);
    if (selectedId === null) {
      return;
    }

    const selectedOption = visibleOptions.find((option) => option.id === selectedId) ?? null;
    if (selectedOption) {
      this.substationSearchTerm = selectedOption.name;
      return;
    }

    if (!clearInvalidSelection) {
      return;
    }

    this.substationSearchTerm = '';
    this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false });
  }

  private getVisibleGeneratorOptions(): PeakReportSubstationOption[] {
    if (!this.isWaterLevelDetailMode()) {
      return [...this.substationOptions];
    }

    return this.substationOptions.filter((option) => this.hasWaterLevelParameter(option.other_parameter));
  }

  private hasWaterLevelParameter(otherParameter: string | null | undefined): boolean {
    const normalizedValue = typeof otherParameter === 'string' ? otherParameter.trim().toLowerCase() : '';
    if (!normalizedValue) {
      return false;
    }

    return normalizedValue
      .split(',')
      .map((item) => item.trim())
      .includes('water level');
  }

  private isWaterLevelDetailMode(): boolean {
    return this.detailMode === 'water-level';
  }

  private extractResponseValue(response: unknown): Record<string, unknown> | null {
    return extractReportResponseValue(response);
  }

  private getArrayValue(value: unknown, key: string): unknown[] {
    return getReportArrayValue(value, key);
  }

  private getRecordValue(value: unknown, key: string): unknown {
    return getReportRecordValue(value, key);
  }

  private getRecord(value: unknown): Record<string, unknown> {
    return getReportRecord(value);
  }

  private extractStringValue(value: unknown, key: string): string | null {
    return extractReportStringValue(value, key);
  }

  private normalizeNumber(value: unknown): number | null {
    return normalizeReportNumber(value);
  }

  private normalizeNullableNumber(value: unknown): number | null {
    return this.normalizeNumber(value);
  }

  private normalizeCellValue(value: string): number | null {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null;
  }

  private getSelectedDateValue(): string {
    return this.selectedDateRequestValue || this.getSelectedDateFormValue();
  }

  private getSelectedDateFormValue(): string {
    const value = this.filtersForm.get('date')?.value;
    return typeof value === 'string' && value ? value : getTodayDateValue();
  }

  protected openDatePicker(): void {
    if (this.isBsDateMode) {
      if (this.bsCalendarOpen) {
        this.closeBsDatePicker();
        return;
      }

      this.openBsDatePicker();
      return;
    }

    const input = this.hiddenDateInput?.nativeElement;
    if (!input) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.click();
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: PeakReportBsCalendarCell): void {
    if (!cell?.ad_date) {
      return;
    }

    this.closeBsDatePicker();
    this.filtersForm.patchValue({ date: cell.ad_date }, { emitEvent: true });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;

    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  private async openBsDatePicker(): Promise<void> {
    this.bsCalendarOpen = true;
    const currentDate = this.getSelectedDateValue() || this.getSelectedDateFormValue();
    const currentKey = this.getSelectedDateValue() === this.selectedDateAdValue ? 'AD' : 'BS';
    await this.loadBsCalendar(currentDate, currentKey);
  }

  private async loadBsCalendar(bsDateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    if (!bsDateValue) {
      return;
    }

    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
          ApiMethod.POST,
          {
            data_date: bsDateValue,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      const calendar = this.extractBsCalendar(response);
      if (calendar) {
        this.bsCalendarMonth = calendar;
      }
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async handleDateSelectionChange(preserveViewState = false): Promise<void> {
    const adDate = this.getSelectedDateFormValue();
    this.selectedDateAdValue = adDate;

    if (this.preferencesSvc.showBsDate) {
      await this.syncBsDateSelection(adDate);
    } else {
      this.selectedDateRequestValue = adDate;
      this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
    }

    this.loadReport(preserveViewState);
  }

  private async syncBsDateSelection(adDate: string): Promise<void> {
    if (!adDate) {
      this.selectedDateRequestValue = getTodayDateValue();
      this.selectedDateDisplayLabel = getTodayDateValue();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: adDate,
            bs_ad_key: 'BS',
          },
          false,
          { suppressToast: true },
        ),
      );
      const converted = this.extractDateConversion(response);
      if (converted) {
        this.selectedDateRequestValue = converted.selected_date || converted.bs_date;
        this.selectedDateDisplayLabel = converted.selected_date_label || converted.bs_date_label;
        return;
      }
    } catch {
      // fall back to AD value below
    }

    this.selectedDateRequestValue = adDate;
    this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
  }

  private extractDateConversion(response: unknown): PeakReportDateConversionResponse | null {
    return extractReportDateConversion(response);
  }

  private extractBsCalendar(response: unknown): PeakReportBsCalendarResponse | null {
    return extractReportBsCalendar(response);
  }

  private createCellKey(stationId: number, blockNo: number): EditableCellKey {
    return `${stationId}-${blockNo}`;
  }

  private buildExportFileName(): string {
    const dateValue = this.getSelectedDateValue();
    return `log_sheet_report_${dateValue}.xlsx`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadReportBlob(blob, fileName);
  }
}
